/**
 * Servicio Observer para notificaciones de Firestore
 * Detecta nuevos documentos en la colección "notificaciones" y los envía a la API externa
 */

import { db } from "../firebase";
import { enviarNotificacionAPI } from "../helpers/NotificacionesAPI";

// Tamaño de la ventana de notificaciones recientes que se mantiene abierta.
// Cualquier documento nuevo entra siempre (es el de `fechahora` más alta).
const VENTANA_NOTIFICACIONES = 20;

class NotificacionesObserver {
  constructor() {
    this.unsubscribe = null;
    this.inicializado = false;
  }

  /**
   * Convierte fechahora a formato string YYYY-MM-DD HH:mm:ss
   * @param {any} fechahora - Timestamp de Firestore, Date, string o number
   * @returns {string} Fecha formateada
   */
  formatearFechahora(fechahora) {
    if (!fechahora) return "";

    if (fechahora.toDate && typeof fechahora.toDate === "function") {
      // Es un Timestamp de Firestore
      const fecha = fechahora.toDate();
      return fecha.toISOString().slice(0, 19).replace("T", " ");
    } else if (typeof fechahora === "string") {
      // Ya es un string
      return fechahora;
    } else if (fechahora instanceof Date) {
      // Es un objeto Date
      return fechahora.toISOString().slice(0, 19).replace("T", " ");
    } else {
      // Intentar convertir desde timestamp
      const fecha = new Date(fechahora);
      return fecha.toISOString().slice(0, 19).replace("T", " ");
    }
  }

  /**
   * Procesa una notificación y la envía a la API externa
   * @param {Object} notificacion - Documento de notificación
   */
  async procesarNotificacion(notificacion) {
    try {
      const payload = {
        caso: notificacion.caso || "",
        cedula: notificacion.cedula || "",
        momento: notificacion.momento || "",
        fechahora: this.formatearFechahora(notificacion.fechahora),
      };

      const resultado = await enviarNotificacionAPI(payload);

      if (resultado.success) {
        console.log(
          "✅ Notificación enviada exitosamente a la API externa:",
          payload
        );
      } else {
        console.error(
          "❌ Error al enviar notificación a la API externa:",
          resultado.error
        );
      }
    } catch (error) {
      console.error(
        "❌ Error al procesar notificación para API externa:",
        error
      );
    }
  }

  /**
   * Inicia el observer de notificaciones
   */
  iniciar() {
    if (this.unsubscribe) {
      console.warn("⚠️ Observer de notificaciones ya está iniciado");
      return;
    }

    console.log("🚀 Iniciando observer de notificaciones...");

    // Solo interesan las notificaciones nuevas. Sin `orderBy`/`limit` esto
    // descarga la colección entera al abrir la app y la mantiene en memoria,
    // compitiendo con el resto de consultas por la misma conexión.
    this.unsubscribe = db
      .collection("notificaciones")
      .orderBy("fechahora", "desc")
      .limit(VENTANA_NOTIFICACIONES)
      .onSnapshot(
        (snapshot) => {
          // Ignorar el primer snapshot que contiene documentos existentes
          if (!this.inicializado) {
            this.inicializado = true;
            console.log("✅ Observer de notificaciones inicializado");
            return;
          }

          // Procesar solo los documentos nuevos agregados después de la inicialización
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              const nuevaNotificacion = {
                id: change.doc.id,
                ...change.doc.data(),
              };

              console.log(
                "📨 Nueva notificación detectada:",
                nuevaNotificacion.id
              );
              this.procesarNotificacion(nuevaNotificacion);
            }
          });
        },
        (error) => {
          console.error("❌ Error en observer de notificaciones:", error);
        }
      );
  }

  /**
   * Detiene el observer de notificaciones
   */
  detener() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
      this.inicializado = false;
      console.log("🛑 Observer de notificaciones detenido");
    }
  }
}

// Exportar instancia singleton
const notificacionesObserver = new NotificacionesObserver();

export default notificacionesObserver;
